import * as React from "react";
import _ from "lodash";
import {
  CellContext,
  ColumnDef,
  createColumnHelper
} from "@tanstack/react-table";

import { ITableProps } from "src/library/components/Tables/Table";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { customCreateColumnHelper } from "src/core/dashboard/components/widgets/table/customColumnHelper";
import { IDrop } from "src/core/drops/api/drops.api";
import { Text } from "src/library/components/Text";
import CustomerDropStatus from "src/core/customers/components/CustomerDropStatus";

export interface IDropTableColumns extends Partial<ITableProps> {
  actionButton: (info: CellContext<IDrop, unknown>) => React.ReactNode;
  terminology: ITerminology;
  // Enables the ability to request specific columns as apposed to all
  selectedColumns?: DropAvailableTableColumn[];
}

export type DropAvailableTableColumn = keyof AvailableDropColumns;

// used to determine what columns can be rendered
interface AvailableDropColumns {
  customer: ColumnDef<IDrop, unknown>;
  dropNumber: ColumnDef<IDrop, unknown>;
  name: ColumnDef<IDrop, unknown>;
  address: ColumnDef<IDrop, unknown>;
  postcode: ColumnDef<IDrop, unknown>;
  status: ColumnDef<IDrop, unknown>;
  lastOrderUpdate: ColumnDef<IDrop, unknown>;
  priceCode: ColumnDef<IDrop, unknown>;
  actions: ColumnDef<IDrop, unknown>;
}

export const getDropTableColumns = (params: IDropTableColumns): Array<ColumnDef<IDrop, unknown>> => {
  const customColumnHelper = customCreateColumnHelper<IDrop>();
  const columnHelper = createColumnHelper<IDrop>();
  const dropStatuses = [ "active", "suspended" ];
  let columns: Array<ColumnDef<IDrop, unknown>>;

  const availableColumns: AvailableDropColumns = {
    customer: customColumnHelper.standardTextColumn(columnHelper, "customer", {
      header: params.terminology.customer.singular.text,
      enableSorting: false,
      cell: info => {
        return (
          <Text>
            {`(${info.row.original.customer?.accountNumber}) ${info.row.original.customer?.name}`}
          </Text>
        );
      }
    }),
    dropNumber: customColumnHelper.standardTextColumn(columnHelper, "branchNumber", {
      header: params.terminology.drop.singular.text,
      cell: info => {
        return (
          <Text>
            {info.row.original.branchNumber}
          </Text>
        );
      }
    }),
    name: customColumnHelper.standardTextColumn(columnHelper, "name", { header: "Name" }),
    address: customColumnHelper.standardTextColumn(columnHelper, "address.addressLine1", {
      header: "Address",
      enableSorting: false
    }),
    postcode: customColumnHelper.standardTextColumn(columnHelper, "address.postcode", {
      header: "Postcode",
      enableSorting: false,
      meta: {
        filter: {
          filterType: "string",
          filterOperation: "none"
        }
      }
    }),
    status: customColumnHelper.standardTextColumn(columnHelper, "status", {
      header: "Status",
      cell: info => {
        return (
          <CustomerDropStatus
            status={info.row.original.status}
          />
        );
      },
      meta: {
        filter: {
          filterType: "select",
          options: dropStatuses.map(status => {
            return {
              text: _.capitalize(status),
              value: status
            };
          })
        }
      }
    }),
    lastOrderUpdate: customColumnHelper.dateColumn(columnHelper, "orderLastUpdatedAt", {
      header: "Last Order Update",
      enableSorting: true,
      enableColumnFilter: false
    }),
    priceCode: customColumnHelper.standardTextColumn(columnHelper, "priceCode", {
      header: "Price Code",
      minSize: 150,
      cell: info => {
        return (
          <Text>
            {info.row.original.priceCode?.name}
          </Text>
        );
      },
      enableSorting: false
    }),
    actions: customColumnHelper.actionColumn(columnHelper, params.actionButton, {})
  };

  // only return selected columns if requested
  if (params.selectedColumns) {
    return params.selectedColumns.map(requestedColumn => availableColumns[ requestedColumn ]);
  } else {
  // otherwise return all by default
    columns = Object.values(availableColumns);
  }

  return columns;
};
